import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  getTokenExpiration,
  setTokens,
  clearSessionAuth,
} from "./Storage";

let inFlightRefresh = null;

export function isTokenExpiringSoon(bufferSeconds = 90) {
  let expMs = null;
  const stored = getTokenExpiration();
  if (stored) {
    const t = Date.parse(stored);
    if (!Number.isNaN(t)) expMs = t;
  }
  if (expMs == null) {
    const token = getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.exp) expMs = payload.exp * 1000;
      } catch {
        /* ignore */
      }
    }
  }
  if (expMs == null) return false;
  return Date.now() >= expMs - bufferSeconds * 1000;
}

/**
 * POST /api/Auth/refresh-token — updates tokens in localStorage.
 * Serialized so concurrent 401s share one refresh.
 */
export function refreshTokens() {
  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      if (!accessToken || !refreshToken) {
        throw new Error("Missing tokens");
      }
      const base = (process.env.REACT_APP_AUTH_API_URL || "").replace(/\/$/, "");
      let data;
      try {
        const res = await axios.post(
          `${base}/api/Auth/refresh-token`,
          { accessToken, refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json, text/plain",
            },
          }
        );
        data = res.data;
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          (typeof e.response?.data === "string" ? e.response.data : null) ||
          e.message ||
          "Token refresh failed";
        throw new Error(msg);
      }
      const ok =
        data &&
        (data.success === true || String(data.success).toLowerCase() === "true");
      if (!ok) {
        throw new Error(data?.message || "Token refresh failed");
      }
      setTokens(
        data.accessToken,
        data.refreshToken,
        data.tokenExpiration,
        data.refreshTokenExpiration
      );
      return data.accessToken;
    })().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

function isRefreshRequestUrl(url) {
  if (!url) return false;
  return url.includes("refresh-token");
}

function isAuthPublicUrl(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  const isClientSignup =
    u.endsWith("/api/client") || u.endsWith("/api/client/");
  return (
    isClientSignup ||
    u.includes("/api/auth/login") ||
    u.includes("/api/auth/forgot-password") ||
    u.includes("/api/auth/reset-password") ||
    u.includes("/api/login/verifyregistrationotp") ||
    u.includes("/api/login/verifyloginotp") ||
    u.includes("/api/authentication/b2bregistration") ||
    u.includes("/api/authentication/savepassword")
  );
}

/**
 * Request: optional proactive refresh; attach Bearer.
 * Response: on 401, refresh once and retry original request.
 */
export function attachTokenRefreshInterceptors(instance) {
  instance.interceptors.request.use(
    async (config) => {
      if (isRefreshRequestUrl(config.url)) {
        return config;
      }
      if (isAuthPublicUrl(config.url)) {
        // Login/OTP/forgot-password endpoints should never trigger token refresh flow.
        return config;
      }
      if (getAccessToken() && getRefreshToken() && isTokenExpiringSoon(120)) {
        try {
          await refreshTokens();
        } catch {
          /* let request proceed; 401 handler may refresh or logout */
        }
      }
      const token = getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      if (!original || original._retry || status !== 401) {
        return Promise.reject(error);
      }

      if (isAuthPublicUrl(original.url)) {
        // For wrong login credentials and other public auth failures,
        // do not redirect/reload page; let screen show API error.
        return Promise.reject(error);
      }

      if (isRefreshRequestUrl(original.url)) {
        clearSessionAuth();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (!getRefreshToken()) {
        clearSessionAuth();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      original._retry = true;
      try {
        await refreshTokens();
        const token = getAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return instance(original);
      } catch {
        clearSessionAuth();
        window.location.href = "/login";
        return Promise.reject(error);
      }
    }
  );
}
